import { useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { createLead, signup } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function LeadCapture() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      await Promise.all([
        createLead({
          name,
          email,
          address: address.trim() || undefined,
          message: message.trim() || undefined,
          source: "landing-form",
        }),
        signup({ name, email }),
      ]);
      setDone(true);
      toast.success("We got your message!", {
        description: "A senior advisor will get back to you the same day.",
      });
      setName("");
      setEmail("");
      setAddress("");
      setMessage("");
    } catch (e) {
      toast.error("Could not submit.", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="lead-section"
      className="relative py-24 sm:py-32 lg:py-40 bg-[#0A0A0A] grain"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-5">
          <div className="data-label mb-6">/ Get in Touch</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight mb-6">
            Talk to a real advisor.
          </h2>
          <p className="text-white/70 text-base sm:text-lg leading-relaxed max-w-md">
            Just fill in your name, email, and the property address you'd like
            us to research. If you need help, a senior advisor is available
            and will get back to you the same day. No sales pressure,
            no spam — just a friendly chat.
          </p>

          <div className="mt-10 space-y-4 font-mono text-[12px] tracking-[0.25em] uppercase">
            <div className="flex items-center gap-3 text-white/55">
              <span className="h-px w-8 bg-gold/60" />
              <a href="mailto:hello@axiompropertyadvisory.com.au" className="hover:text-gold transition-colors">
                hello@axiompropertyadvisory.com.au
              </a>
            </div>
            <div className="flex items-center gap-3 text-white/55">
              <span className="h-px w-8 bg-gold/60" />
              <a href="tel:1300713180" data-testid="lead-phone" className="hover:text-gold transition-colors">
                1300 713 180
              </a>
            </div>
            <div className="flex items-center gap-3 text-white/55">
              <span className="h-px w-8 bg-gold/60" />
              Australia wide
            </div>
          </div>
        </div>

        <form
          data-testid="lead-form"
          onSubmit={handleSubmit}
          className="lg:col-span-6 lg:col-start-7 border border-white/10 bg-[#0F0F0F] p-8 sm:p-12 space-y-8"
        >
          <div>
            <label className="data-label block mb-3">/ Your Name</label>
            <Input
              data-testid="lead-name-input"
              required
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Smith"
              className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 h-auto text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-gold text-base"
            />
          </div>
          <div>
            <label className="data-label block mb-3">/ Email Address</label>
            <Input
              data-testid="lead-email-input"
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 h-auto text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-gold text-base"
            />
          </div>
          <div>
            <label className="data-label block mb-3">/ Property Address You'd Like Us To Research</label>
            <Input
              data-testid="lead-address-input"
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="e.g. 14 Beach Road, Bondi NSW 2026"
              className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 h-auto text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-gold text-base"
            />
          </div>
          <div>
            <label className="data-label block mb-3">/ Tell us a bit about you (optional)</label>
            <Textarea
              data-testid="lead-message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="What are you hoping to do? How much have you saved? Any questions?"
              className="bg-transparent border border-white/15 rounded-none p-4 text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-gold resize-none"
            />
          </div>

          <button
            type="submit"
            data-testid="lead-submit-button"
            disabled={submitting}
            className="group w-full inline-flex items-center justify-center gap-3 bg-gold text-ink py-4 text-[11px] tracking-[0.3em] uppercase font-semibold hover:bg-gold-dark transition-colors disabled:opacity-60"
          >
            {submitting ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <>
                {done ? "Send Another Message" : "Send Message"}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </button>

          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 leading-relaxed">
            By sending this you agree to be contacted by Axiom and to our{" "}
            <a href="/terms" data-testid="lead-terms-link" className="text-gold/80 hover:text-gold normal-case tracking-normal underline underline-offset-2">
              Terms &amp; Conditions
            </a>
            . We never sell or share your details.
          </p>
        </form>
      </div>
    </section>
  );
}
